/**
 * OptionsDialogContent - Content component for options dialog
 * Now with Cancel/Save buttons instead of immediate application
 */

import type { Component, ChangeEvent } from 'react';
import { isLocalFileContext } from '../utils/Utilities';

// React is available globally from CDN
const React = (window as any).React;
const ReactComponent = React.Component;

interface OptionsDialogContentProps {
    onSave: () => void;
    onCancel: () => void;
}

interface OptionsDialogContentState {
    musicVolume: number;
    sfxVolume: number;
    historyEnabled: boolean;
    historySize: number;
    debugEnabled: boolean;
    darkMode: boolean;
    enablePreloading: boolean;
}

class OptionsDialogContent extends ReactComponent<OptionsDialogContentProps, OptionsDialogContentState> {
    private originalState: OptionsDialogContentState;
    private readonly localFileContext: boolean = isLocalFileContext();

    constructor(props: OptionsDialogContentProps) {
        super(props);
        const Settings = (window as any).Settings;
        
        // Read current settings immediately in constructor
        // For dark mode, check the DOM as it's the source of truth
        const isDarkMode = document.body.classList.contains('dark-mode');
        
        // Initialize with current settings
        this.originalState = {
            musicVolume: Settings?.musicVolume ?? 100,
            sfxVolume: Settings?.sfxVolume ?? 100,
            historyEnabled: Settings?.historyEnabled ?? false,
            historySize: Settings?.historySize ?? 100,
            debugEnabled: Settings?.debugEnabled ?? false,
            darkMode: isDarkMode,
            enablePreloading: this.localFileContext ? false : (Settings?.enablePreloading ?? true)
        };
        this.state = { ...this.originalState };
    }

    componentDidMount(): void {
        // Re-read to ensure we have the latest values (in case Settings changed)
        const Settings = (window as any).Settings;
        if (Settings) {
            // For dark mode, check the DOM first as it's the source of truth
            const isDarkMode = document.body.classList.contains('dark-mode');
            
            const currentState = {
                musicVolume: Settings.musicVolume,
                sfxVolume: Settings.sfxVolume,
                historyEnabled: Settings.historyEnabled,
                historySize: Settings.historySize,
                debugEnabled: Settings.debugEnabled,
                darkMode: isDarkMode,
                enablePreloading: this.localFileContext ? false : (Settings.enablePreloading ?? true)
            };
            this.originalState = currentState;
            this.setState(currentState);
        }
        
        // Listen for settings changes from other tabs
        window.addEventListener('settingsChanged', this.handleSettingsChanged as EventListener);
    }

    componentWillUnmount(): void {
        window.removeEventListener('settingsChanged', this.handleSettingsChanged as EventListener);
    }

    handleSettingsChanged = (e: CustomEvent): void => {
        const Settings = (window as any).Settings;
        if (!Settings) return;
        
        const detail = e.detail;
        if (detail.name === 'darkMode') {
            const isDarkMode = document.body.classList.contains('dark-mode');
            this.setState({ darkMode: isDarkMode });
            this.originalState = { ...this.originalState, darkMode: isDarkMode };
        } else if (detail.name === 'musicVolume') {
            this.setState({ musicVolume: detail.value });
            this.originalState = { ...this.originalState, musicVolume: detail.value };
        } else if (detail.name === 'sfxVolume') {
            this.setState({ sfxVolume: detail.value });
            this.originalState = { ...this.originalState, sfxVolume: detail.value };
        } else if (detail.name === 'historyEnabled') {
            this.setState({ historyEnabled: detail.value });
            this.originalState = { ...this.originalState, historyEnabled: detail.value };
        } else if (detail.name === 'historySize') {
            this.setState({ historySize: detail.value });
            this.originalState = { ...this.originalState, historySize: detail.value };
        } else if (detail.name === 'debugEnabled') {
            this.setState({ debugEnabled: detail.value });
            this.originalState = { ...this.originalState, debugEnabled: detail.value };
        } else if (detail.name === 'enablePreloading') {
            this.setState({ enablePreloading: detail.value });
            this.originalState = { ...this.originalState, enablePreloading: detail.value };
        }
    }

    handleChange = (e: ChangeEvent<HTMLInputElement>): void => {
        const field = e.target;

        switch (field.name) {
            case 'music-volume':
            case 'music-volume-slider': {
                const value = parseInt(field.value);
                this.setState({ musicVolume: value });
                // Sync the other field
                const otherField = document.querySelector(
                    `input[name="${field.name === 'music-volume' ? 'music-volume-slider' : 'music-volume'}"]`
                ) as HTMLInputElement;
                if (otherField) otherField.value = String(value);
                break;
            }

            case 'sfx-volume':
            case 'sfx-volume-slider': {
                const value = parseInt(field.value);
                this.setState({ sfxVolume: value });
                // Sync the other field
                const otherField = document.querySelector(
                    `input[name="${field.name === 'sfx-volume' ? 'sfx-volume-slider' : 'sfx-volume'}"]`
                ) as HTMLInputElement;
                if (otherField) otherField.value = String(value);
                break;
            }

            case 'enable-history': {
                this.setState({ historyEnabled: field.checked });
                break;
            }

            case 'history-size': {
                const value = parseInt(field.value);
                this.setState({ historySize: value });
                break;
            }

            case 'enable-debug': {
                this.setState({ debugEnabled: field.checked });
                break;
            }

            case 'enable-darkmode': {
                this.setState({ darkMode: field.checked });
                break;
            }

            case 'enable-preloading': {
                this.setState({ enablePreloading: field.checked });
                break;
            }
        }
    };

    handleSave = (): void => {
        const Settings = (window as any).Settings;
        const GameHistory = (window as any).GameHistory;
        const GameUI = (window as any).GameUI;
        const BGMusic = document.getElementById('BGMusic') as HTMLAudioElement;
        const SoundEffect = document.getElementById('SoundEffect') as HTMLAudioElement;
        const backButton = document.getElementById('back') as HTMLButtonElement;

        // Apply all settings
        if (Settings) {
            Settings.musicVolume = this.state.musicVolume;
            Settings.sfxVolume = this.state.sfxVolume;
            Settings.historyEnabled = this.state.historyEnabled;
            Settings.historySize = this.state.historySize;
            Settings.debugEnabled = this.state.debugEnabled;
            Settings.darkMode = this.state.darkMode;
            Settings.enablePreloading = this.localFileContext ? false : this.state.enablePreloading;
        }

        // Apply audio settings
        if (BGMusic) {
            const volume = this.state.musicVolume / 100;
            BGMusic.volume = volume;
            BGMusic.muted = volume == 0;
        }
        if (SoundEffect) {
            const volume = this.state.sfxVolume / 100;
            SoundEffect.volume = volume;
            SoundEffect.muted = volume == 0;
        }

        // Apply history settings
        if (GameHistory) {
            GameHistory.enabled = this.state.historyEnabled;
            GameHistory.MAX_HISTORY_SIZE = this.state.historySize;
            if (!this.state.historyEnabled) {
                GameHistory.reset();
                if (backButton) backButton.disabled = true;
            }
            if (backButton) backButton.title = GameHistory.noGoBackReason();
        }

        // Apply dark mode
        if (GameUI) {
            GameUI.setDarkMode(this.state.darkMode);
        }

        if (this.props.onSave) {
            this.props.onSave();
        }
    };

    handleCancel = (): void => {
        // Revert to original state
        this.setState({ ...this.originalState });
        if (this.props.onCancel) {
            this.props.onCancel();
        }
    };

    render(): any {
        return React.createElement('form', {
            id: 'options-form',
            onSubmit: (e: any) => {
                e.preventDefault();
            },
            onChange: this.handleChange,
            onInput: (e: any) => {
                const field = e.target as HTMLInputElement;
                if (field.name.endsWith('-slider')) {
                    this.handleChange(e);
                }
            },
            style: {
                display: 'flex',
                flexDirection: 'column',
                gap: '12px' // Reduced spacing
            }
        },
            React.createElement('label', { style: { display: 'flex', alignItems: 'center', gap: '12px' } },
                React.createElement('span', { style: { flexGrow: 1 } }, 'Music volume:'),
                React.createElement('input', {
                    type: 'number',
                    name: 'music-volume',
                    min: 0,
                    max: 100,
                    step: 1,
                    value: this.state.musicVolume,
                    style: { width: '80px' }
                }),
                React.createElement('span', null, ' (%)'),
                React.createElement('input', {
                    type: 'range',
                    name: 'music-volume-slider',
                    min: 0,
                    max: 100,
                    step: 1,
                    value: this.state.musicVolume,
                    style: { flex: '1', maxWidth: '200px' }
                })
            ),
            React.createElement('label', { style: { display: 'flex', alignItems: 'center', gap: '12px' } },
                React.createElement('span', { style: { flexGrow: 1 } }, 'SFX volume:'),
                React.createElement('input', {
                    type: 'number',
                    name: 'sfx-volume',
                    min: 0,
                    max: 100,
                    step: 1,
                    value: this.state.sfxVolume,
                    style: { width: '80px' }
                }),
                React.createElement('span', null, ' (%)'),
                React.createElement('input', {
                    type: 'range',
                    name: 'sfx-volume-slider',
                    min: 0,
                    max: 100,
                    step: 1,
                    value: this.state.sfxVolume,
                    style: { flex: '1', maxWidth: '200px' }
                })
            ),
            React.createElement('label', { style: { display: 'flex', alignItems: 'center', gap: '8px' } },
                React.createElement('input', {
                    type: 'checkbox',
                    name: 'enable-history',
                    checked: this.state.historyEnabled
                }),
                React.createElement('span', null, 'Enable history (back button)')
            ),
            React.createElement('label', { style: { display: 'flex', alignItems: 'center', gap: '8px' } },
                React.createElement('span', null, 'History size: '),
                React.createElement('input', {
                    type: 'number',
                    name: 'history-size',
                    min: 1,
                    step: 1,
                    value: this.state.historySize,
                    style: { width: '80px' }
                }),
                React.createElement('span', null, ' (states)')
            ),
            React.createElement('label', { style: { display: 'flex', alignItems: 'center', gap: '8px' } },
                React.createElement('input', {
                    type: 'checkbox',
                    name: 'enable-darkmode',
                    checked: this.state.darkMode
                }),
                React.createElement('span', null, 'Dark mode')
            ),
            React.createElement('label', { style: { display: 'flex', alignItems: 'center', gap: '8px' } },
                React.createElement('input', {
                    type: 'checkbox',
                    name: 'enable-debug',
                    checked: this.state.debugEnabled
                }),
                React.createElement('span', null, 'Debug mode')
            ),
            React.createElement('label', { style: { display: 'flex', alignItems: 'center', gap: '8px' } },
                React.createElement('input', {
                    type: 'checkbox',
                    name: 'enable-preloading',
                    checked: this.state.enablePreloading,
                    disabled: this.localFileContext
                }),
                React.createElement('span', null, 'Enable preloading')
            )
        );
    }
}

(window as any).OptionsDialogContent = OptionsDialogContent;
export default OptionsDialogContent;
